import { ActionIcon, ActionIconProps, Tooltip, useMantineTheme } from '@mantine/core';
import { forwardRef, ReactNode } from 'react';

export type GradientVariant =
  | 'primary'
  | 'secondary'
  | 'tertiary'
  | 'success'
  | 'info'
  | 'warning'
  | 'danger';

export interface IconButtonProps extends Omit<ActionIconProps, 'variant'> {
  icon: ReactNode;
  tooltip?: string;
  variant?: 'gradient' | 'filled' | 'light' | 'outline' | 'subtle' | 'transparent' | 'default';
  gradientVariant?: GradientVariant;
  /** Polymorphic component to render as (e.g., 'a', Link) */
  component?: any;
  /** Route path when using component prop with React Router Link */
  to?: string;
  /** Standard anchor href for direct links (when component="a") */
  href?: string;
  /** Target for anchor links (when component="a") */
  target?: string;
  /** Rel attribute for anchor links (when component="a") */
  rel?: string;
}

const IconButton = forwardRef<HTMLButtonElement, IconButtonProps>(
  ({ icon, tooltip, variant = 'subtle', gradientVariant = 'primary', style, ...rest }, ref) => {
    const theme = useMantineTheme();

    const button = (
      <ActionIcon
        ref={ref}
        variant={variant === 'gradient' ? 'filled' : variant}
        style={
          variant === 'gradient'
            ? {
                backgroundImage: `linear-gradient(135deg, ${theme.other.gradients[gradientVariant].from}, ${theme.other.gradients[gradientVariant].to})`,
                color: 'white',
                border: 'none',
                ...style,
              }
            : style
        }
        {...rest}
      >
        {icon}
      </ActionIcon>
    );

    if (tooltip) {
      return <Tooltip label={tooltip}>{button}</Tooltip>;
    }

    return button;
  }
);

IconButton.displayName = 'IconButton';

export default IconButton;
